<template lang="pug">
    .relative.w-full(v-clickoutside='close')
      button.transition.duration-200.ease-out.relative.w-full.text-left.truncate(
        :class='[customClasses, disabledClass]'
        ref='menu'
        @click="toggleDropdown()"
        @keydown.esc='close()'
        :disabled='disabled'
        @focus="$emit('focus')"
        @blur="$emit('blur')"
      ) {{ currentValue }}
      span.ml-3.absolute.inset-y-0.right-0.flex.items-center.pr-2.pointer-events-none.pt-2
        sw-icon.text-gray-600(name="expand" size='h-6 w-6' unstyled)

      div.absolute(:class='dropdownClasses' v-show='isOpen' ref='dropdown')
        //- Query search input
        slot(name='query-input' :close='close' :query='query', :handleQuery='handleQuery')
          //- Default block if not overwritten in the parent (hidden on mobile)
          .hidden(class='md:flex' v-if='filterable')
            input.relative.m-2.p-2.w-full.appearance-none.border.border-gray-300.rounded-sm.text-xs(
              :value='query'
              @input='handleQuery($event.target.value)'
              @keydown.esc='close()'
            )

        //- Block within the dropdown
        ul.text-base.overflow-auto(class="focus:outline-none sm:text-sm max-h-screen-14/50 md:max-h-56")
          li.my-2(
            v-for="(group, groupIndex) in groupedOptions"
            :key="group.label"
          )
            ul
              span.pl-4.text-gray-300.text-xs.uppercase(v-show='isGroupedOption') {{ group.label }}
              li.cursor-pointer.select-none.relative(
                v-for='(item, i) in filteredOptions(group.options)'
                :key='item.value'
                :id='item.value'
                @click="handleSelect(item.value)"
                class='hover:bg-gray-600'
              )
                slot.min-h-10.flex.items-center(:isSelectedItem='isSelectedItem' :item='item')
                  //- Default block if not overwritten in the parent
                  span.ml-3.block.font-medium(
                    class='md:text-lg hover:text-white'
                    :class="{'font-bold': isSelectedItem(item.value)}"
                  ) {{ item.label || item.value }}
                  span.absolute.inset-y-0.right-0.flex.items-center.pr-4(v-if='isSelectedItem(item.value)')
                    sw-icon(name='check' size='h-8 w-8')

            div.p-4(v-show='filteredOptions(group.options).length === 0') {{ noResults }}
            .flex.justify-center(v-if='groupIndex + 1 != options.length && isGroupedOption')
              hr.my-2.border-t.border-gray-300(class='w-4/5')
</template>

<script>
import { responsiveWatcher } from '../../mixins/responsiveWatcher';
import { createPopper } from '@popperjs/core';

export default {
  name: 'SwSelect',
  mixins: [responsiveWatcher],
  props: {
    options: {
      type: [Array, Boolean],
      required: true,
    },
    value: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      required: true,
    },
    customClasses: {
      type: String,
      default: '',
    },
    dropdownClasses: {
      type: String,
      default: 'bg-gray-100',
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    dropdownOpen: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'bottom',
      validator: value => [
        'auto',
        'auto-start',
        'auto-end',
        'top',
        'top-start',
        'top-end',
        'bottom',
        'bottom-start',
        'bottom-end',
        'right',
        'right-start',
        'right-end',
        'left',
        'left-start',
        'left-end',
       ].includes(value),
    },
  },
  data () {
    return {
      query: '',
      isOpen: false,
    };
  },
  computed: {
    isGroupedOption () {
      return this.options[0]?.options;
    },
    groupedOptions () {
      return this.isGroupedOption ? this.options : [{ options: this.options }];
    },
    currentValue () {
      return this.currentLabel || this.value || this.placeholder;
    },
    currentLabel () {
      if (this.isGroupedOption) return ''; // No handling at the moment of a :groupedOptions with labels. To be implemented if needed.
      return this.options?.find(item => item.value === this.value)?.label;
    },
    disabledClass () {
      return this.disabled ? 'not-allowed': 'cursor-pointer';
    },
    noResults () {
      return this.$t('shared.common._noResults') + ` - "${this.query.toUpperCase()}"`;
    },
  },
  mounted () {
    this.$nextTick(() => {
      this.isOpen = this.dropdownOpen;
      this.focusOnQueryField();
    });
  },
  beforeDestroy () {
    this.popper?.destroy();
  },
  methods: {
    handleQuery (value) {
      this.query = value;
    },
    filteredOptions (options) {
      return options.filter(option => option.value.toLowerCase().includes(this.query.toLowerCase()));
    },
    isSelectedItem (value) {
      return this.value === value;
    },
    toggleDropdown () {
      this.query = '';
      this.isOpen ? this.close() : this.open();
    },
    handleSelect (value) {
      this.$emit('input', value);
      this.close();
    },
    open () {
      this.isOpen = true;
      this.focusOnQueryField();
      this.$nextTick(() => {
        this.setupPopper();
      });
    },
    close () {
      this.query = '';
      this.isOpen = false;
    },
    focusOnQueryField () {
      this.$nextTick(() => {
        if (!this.isOpen) return;
        // Impossible to use the $refs for the input field as it's defined in slot in the parent → getElementsByTagName
        this.$refs.dropdown.getElementsByTagName('input')[0]?.focus();
      });
    },
    setupPopper () {
      if (!this.isMobile) return;
      if (this.popper === undefined) {
        this.popper = createPopper(this.$refs.menu, this.$refs.dropdown, {
          placement: this.placement,
          strategy: 'absolute',
          modifiers: [
            {
              name: 'computeStyles',
              options: { adaptive: false },
            },
            {
              name: 'offset',
              options: { offset: [0, 6] },
            },
          ],
        });
      } else {
        this.popper.update();
      }
    },
  },
};
</script>
